// src/pages/ThankYouPage.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";

function ThankYouPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);

  const { clearCart } = useCart();

  useEffect(() => {
    const data = sessionStorage.getItem("esill-order");
    if (data) setOrder(JSON.parse(data));
  }, []);

   useEffect(() => {
  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_thank_you",
      entity_type: "order",
      entity_id: order?.order_id || null,
    }),
  }).catch(() => {});
}, [order?.order_id]);

useEffect(() => {
  if (!order?.order_id) return;

  const eventId = `purchase_${order.order_id}`;

  // ✅ Browser Pixel (with eventID)
  if (typeof window.fbq === "function") {
    window.fbq(
  "track",
  "Purchase",
  {
    content_ids: order.items?.map(i => i.public_id) || [],
    content_type: "product",
    value: Number(order.total),
    currency: "USD",
    num_items: Number(order.itemCount),
  },
  { eventID: eventId }
);
  }

  // ✅ Server-side Conversions API
  apiRequest("/analytics/purchase", {
    method: "POST",
    body: JSON.stringify({
      event_id: eventId,
      order_id: order.order_id,
      value: Number(order.total) || 0,
      email: order.email || null,
      phone: order.phone || null,
    }),
  }).catch(() => {});
}, [order?.order_id]);


  if (!order) return null;



  const TEXT = {
    EN: {
      thanks: "Thank You!",
      loginInfo: "These are your Esiell account login details:",
      name: "Full Name",
      phone: "Phone Number",
      email: "Email",
      noEmail: "No email provided",
      city: "Governorate",
      address: "Address",
      credentialsNote:
        "We will use this information to prepare your Esiell customer account for future logins.",
      ok: "OK",
      summary: "Order Summary",
      products: "Products",
      delivery: "Delivery",
      total: "Total",
    },
    AR: {
      thanks: "شكرًا لك!",
      loginInfo: "هذه هي بيانات تسجيل الدخول الخاصة بحسابك في Esiell:",
      name: "الاسم الكامل",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني",
      noEmail: "لم يتم إدخال بريد إلكتروني",
      city: "المحافظة",
      address: "العنوان",
      credentialsNote:
        "سنستخدم هذه المعلومات لإنشاء حساب زبون لك في Esiell لتسجيل الدخول مستقبلاً.",
      ok: "حسنًا",
      summary: "ملخص الطلب",
      products: "عدد المنتجات",
      delivery: "التوصيل",
      total: "الإجمالي",
    },
  };

  const t = TEXT[lang];

  const getDeliveryMessage = () => {
  if (order.shipping_method === "pickup") {
    return lang === "EN"
      ? "Your order will be ready for pickup from the store"
      : "طلبك سيكون جاهزاً للاستلام من المتجر";
  }

  return lang === "EN"
    ? "Your order will be delivered within 2 to 3 days"
    : "سيتم توصيل طلبك خلال يومين إلى ثلاثة أيام";
};


  return (
    <div
      className="w-full min-h-screen flex flex-col items-center justify-center px-6 text-center bg-white"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* ANIMATED TITLE */}
      <h1 className="text-4xl font-bold mb-4 animate-bounce">{t.thanks}</h1>

      {/* ORDER SUMMARY BOX */}
      <div className="w-full max-w-md border border-[#E6D8D2] rounded-lg p-6 shadow-sm mb-6">
        <p className="font-medium text-lg mb-4">{t.summary}</p>

        <div className="checkout-line">
          <span>{t.products}</span>
          <span>{order.itemCount}</span>
        </div>

        <div className="checkout-line">
          <span>Subtotal</span>
          <span>${order.subtotal}</span>
        </div>

        {order.discount > 0 && (
          <div className="checkout-line">
            <span>Discount</span>
            <span>-${order.discount}</span>
          </div>
        )}

       <div className="checkout-line">
  <span>
    {order.shipping_method === "pickup"
      ? lang === "EN"
        ? "Pickup from store"
        : "استلام من المتجر"
      : t.delivery}
  </span>

  <span
    className={
      order.shipping_method === "pickup"
        ? "font-bold text-green-600"
        : order.delivery_fee === 0
        ? "font-bold text-red-600"
        : ""
    }
  >
    {order.shipping_method === "pickup"
      ? lang === "EN"
        ? "Free"
        : "مجاني"
      : `$${Number(order.delivery_fee || 0).toFixed(2)}`}
  </span>
</div>



        <hr className="my-3" />

        <div className="checkout-line font-bold text-lg">
          <span>{t.total}</span>
          <span>${order.total}</span>
        </div>
        <p className="text-sm text-[#2E3135] mt-3 font-medium">
            {getDeliveryMessage()}
          </p>
      </div>

      {/* LOGIN INFORMATION */}
      <div className="w-full max-w-md border border-[#E6D8D2] rounded-lg p-6 shadow-sm mb-6 text-left">
        <p className="font-medium text-lg mb-4 text-center">{t.loginInfo}</p>

        <div className="checkout-line">
          <span>{t.name}</span>
          <span>{order.full_name}</span>
        </div>

        <div className="checkout-line">
          <span>{t.phone}</span>
          <span dir="ltr">{order.phone}</span>
        </div>

        <div className="checkout-line">
          <span>{t.email}</span>
          <span>{order.email ? order.email : t.noEmail}</span>
        </div>

        <div className="checkout-line">
          <span>{t.city}</span>
          <span>{order.city}</span>
        </div>

        <div className="checkout-line">
          <span>{t.address}</span>
          <span>
             {order.street}
           </span>
        </div>

        <p className="text-sm text-[#7A7674] mt-4 leading-6 text-center">
          {t.credentialsNote}
        </p>
      </div>

      {/* OK BUTTON */}
      <button
  onClick={() => {
    clearCart();                 // ✅ instantly empty cart UI
    sessionStorage.removeItem("esill-order"); // optional cleanup
    navigate("/");
  }}
  className="px-10 py-3 bg-black text-white rounded-md text-lg font-semibold hover:bg-[#222] active:scale-95 transition"
>
  {t.ok}
</button>

    </div>
  );
}

export default ThankYouPage;
